import { Component, NgZone, ViewChild, isDevMode } from '@angular/core';
import { IonApp, IonMenu, IonRouterOutlet, IonContent } from '@ionic/angular/standalone';
import { MenuController, Platform } from '@ionic/angular';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { HttpClient } from '@angular/common/http';
import { UpdateService } from './services/update.service';

import { Location } from '@angular/common';

import { App, URLOpenListenerEvent } from '@capacitor/app';
import { StatusBar, Style} from '@capacitor/status-bar';
import { register } from 'swiper/element/bundle';
register();
import { Device } from '@capacitor/device';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { SidemenuComponent } from './shared-module/components/sidemenu/sidemenu.component';
import { TranslateService } from '@ngx-translate/core';
import { ErrorHandlerUtil } from './utils/error-handler.util';
import { AuthService } from './core/services/auth.service';
import { DeepLinkService } from './core/services/deep-link.service';

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    standalone: true,
    imports: [IonApp, IonMenu, IonRouterOutlet, IonContent, SidemenuComponent]
})
export class AppComponent {
  @ViewChild(IonRouterOutlet, { static: true }) routerOutlet: IonRouterOutlet;
  @ViewChild(IonMenu, { static: true }) menu: IonMenu;

  constructor(
    private menuController: MenuController,
    private router: Router,
    private platform: Platform,
    private updateService: UpdateService,
    private location: Location,
    private zone: NgZone,
    private translateService: TranslateService,
    private titleService: Title,
    private http: HttpClient,
    private authService: AuthService,
    private deepLink: DeepLinkService,
  ) {
    ErrorHandlerUtil.setTranslateService(this.translateService);
    this.setWebAppTitle();
    this.initializeApp();
    // Only check for updates in production mode on native platforms
    if (Capacitor.isNativePlatform() && !isDevMode()) {
      this.platform.ready().then(() => {
        this.updateService.checkForUpdate();
      });
    }

    this.backButtonEvent();
  }

  /**
   * On the web, set the browser-tab title from the per-client PWA manifest
   * (manifest.webmanifest is swapped per white-label at build time). Native
   * apps show their own app name, so this is web-only.
   */
  private setWebAppTitle(): void {
    if (Capacitor.isNativePlatform()) {
      return;
    }
    this.http.get<{ name?: string }>('/manifest.webmanifest').subscribe({
      next: (manifest) => {
        if (manifest?.name) {
          this.titleService.setTitle(manifest.name);
        }
      },
      error: () => { /* keep the static <title> fallback */ }
    });
  }

  initializeApp() {
    App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      this.zone.run(() => {
        const url = new URL(event.url);
        this.router.navigateByUrl(url.pathname + url.search); // e.g. /register?code=..&sta=..
      });
    });

    this.platform.ready().then(async () => {
      if (!this.platform.is('hybrid')) {
        // On the web the browser reports zero insets, so leave the defaults.
        return;
      }

      await this.setupSystemBars();

      // ponytail: dormant deferred-deep-link hook — inert until D1 server endpoint is deployed.
      // Gates: native platform + not authenticated + first run flag not already set.
      const isAuthenticated = this.authService.isAuthenticated.getValue();
      if (Capacitor.isNativePlatform() && !isAuthenticated) {
        const firstRunPref = await Preferences.get({ key: 'dl_first_run_done' });
        if (!firstRunPref.value) {
          await Preferences.set({ key: 'dl_first_run_done', value: '1' });
          await this.deepLink.resumeFromInstall();
        }
      }
    });
  }

  /**
   * Configure StatusBar based on platform.
   * iOS: overlay enabled - the WebView renders behind the status bar, so the
   *      CSS safe area is what pushes the header down.
   * Android: overlay disabled below API 35 (Android 15), where the system
   *      insets the WebView itself. From API 35 on the window is permanently
   *      edge-to-edge and setOverlaysWebView(false) is a no-op anyway (the
   *      plugin implements it with SYSTEM_UI_FLAG_LAYOUT_FULLSCREEN and
   *      setStatusBarColor, both ignored from 35 on).
   */
  private async setupSystemBars(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    try {
      if (Capacitor.getPlatform() === 'android') {
        // Treat an unknown SDK as edge-to-edge: keeping the safe-area padding
        // is safer than risking content underneath the status bar.
        const { androidSDKVersion } = await Device.getInfo();
        const edgeToEdge = (androidSDKVersion ?? 35) >= 35;

        await StatusBar.setOverlaysWebView({ overlay: edgeToEdge });
        if (!edgeToEdge) {
          await StatusBar.setBackgroundColor({ color: '#FFFFFF' });
          // The WebView already starts below the status bar, but it still
          // reports a non-zero env(safe-area-inset-top). Leaving it would
          // offset the first toolbar a second time (measured: 27dp of dead
          // space above the header).
          document.documentElement.style.setProperty('--ion-safe-area-top', '0px');
        }
      } else {
        await StatusBar.setOverlaysWebView({ overlay: true });
      }

      await StatusBar.setStyle({ style: Style.Light });
    } catch (error) {
      console.warn('StatusBar configuration failed', error);
    }
  }

  backButtonEvent() {
    this.platform.backButton.subscribeWithPriority(10, () => {
      if (!this.routerOutlet.canGoBack()) {
        navigator['app'].exitApp();
      } else {
        var currentPage = this.router.url;
        if (currentPage === '/dashboard/tabs/home' || currentPage === '/login') {
          navigator['app'].exitApp();
        } else {
          this.location.back();
        }
      }
    });
  }
}